import React, {Fragment} from 'react';
import {HexColorInput} from 'react-colorful';
import styled from 'styled-components';

import ColorPicker from '../ColorPicker';

function Input(props, ref) {
  const {id, label, labelPosition = 'top', inline, helpText, invalid, validationText, required, large, style, className, ...inputProps} = props;

  if (label && !id) {
    throw new Error('When setting a label on an <Input />, you must also include an id.');
  }

  if (labelPosition !== 'top' && labelPosition !== 'start') {
    throw new Error('Valid label positions are top and start');
  }
  
  return (
    <Wrapper inline={inline}>
      <InputContainer inline={inline} labelPosition={labelPosition} style={style} className={className}>
        {label && (
          <Label htmlFor={id} labelPosition={labelPosition}>{label + (required ? ' *' : '')}</Label>
        )}
        <InputHeightWrapper large={large}>
          <InputControl ref={ref} id={id} name={id} invalid={invalid ? true : undefined} large={large} {...inputProps} />
        </InputHeightWrapper>
      </InputContainer>
      {((invalid && validationText) || helpText) && (
        <Help invalid={invalid ? true : undefined}>{invalid ? validationText : helpText}</Help>
      )}
    </Wrapper>
  );
}

const Wrapper = ({inline, ...props}) => inline ? <Fragment {...props} /> : <div {...props} />;

const InputContainer = styled.div`
  display: ${props => props.inline ? 'inline-flex' : 'flex'};
  flex-direction: ${props => props.labelPosition === 'top' ? 'column' : 'row'};
`;

const InputHeightWrapper = styled.div`
  height: ${props => props.large ? 'calc(2.875rem + 2px)' : 'calc(2.25rem + 2px)'};
`;

const ColorContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
`

const InputControl = styled(React.forwardRef(({large, invalid, ...props}, ref) => {
  if (props.type === 'select') {
    return <select ref={ref} {...props} />;
  }

  if (props.type === 'color') {
    const {type, ...otherProps} = props;
    return (
      <ColorContainer>
        <HexColorInput {...otherProps} />
        <ColorPicker {...otherProps}/>
      </ColorContainer>
    );
  }
  
  return <input ref={ref} {...props} />;
}))`
  flex: 1;
  display: block;
  width: 100%;
  height: ${props => props.type === 'file' ? "auto" : "calc(2.25rem + 2px)"};
  padding: .375rem .75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: ${props => props.type === 'color' ? '.25rem 0 0 .25rem' : '.25rem'};
  transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;

  :disabled {
    background-color: #EBEBEB;
    opacity: 1;
  }

  :focus {
    color: #495057;
    background-color: #fff;
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, .25);
  }

  // Handle the large variant of inputs
  ${props => {
    if (props.large) {
      return `
        height: calc(2.875rem + 2px);
        padding: .5rem 1rem;
        font-size: 1.25rem;
        line-height: 1.5;
        border-radius: .3rem;
      `;
    }
  }}

  // Handle validation state
  ${props => {
    if (props.invalid) {
      return `
        border-color: #dc3545 !important;

        :focus {
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, .25);
        }
      `;
    }
  }}
`;

const Label = styled.label`
  ${props => {
    if (props.labelPosition === 'top') {
      return `
        display: block;
        margin-bottom: 0.5rem;
      `;
    }

    return `
      word-wrap: nowrap;
      margin-right: 1rem;
      display: flex;
      align-items: center;
    `;
  }}
`;

const Help = styled.small`
  display: block;
  font-size: 0.875rem;
  color: #747474;
  margin-top: 0.5rem;

  ${props => {
    if (props.invalid) {
      return `
        color: #dc3545;
      `;
    }
  }}
`;

export default React.forwardRef(Input);